import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ServiceOrderApiService } from '../shared/services/apis/service-order-api.service';
import { AuthService } from '../shared/services/apis/auth.service';
import { Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs/internal/Subscription';
import { DiscountItemAction, OsApprovalCardComponent, ServiceOrder, ServiceOrderItem } from '../shared/components/os-approval-card/os-approval-card.component';
import { debounceTime, distinctUntilChanged, filter, map, take, tap } from 'rxjs';
import { oSItensLists, ServiceOrderListItemDto } from '../shared/services/responses/service-order-list-item.dto';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ProfileMenuComponent } from '../shared/components/profile-menu/profile-menu.component';
import { LoaderComponent } from '../shared/components/loader/loader.component';
import { ApprovalOSRequest } from '../shared/services/request/approval-os.request';

@Component({
  selector: 'app-os-approval',
  imports: [CommonModule, ReactiveFormsModule, OsApprovalCardComponent, ProfileMenuComponent, LoaderComponent],
  templateUrl: './os-approval.component.html',
  styleUrl: './os-approval.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OSApprovalComponent implements OnInit, OnDestroy {
    private searchSubscription?: Subscription;
    private loadOrdersSubscription?: Subscription;
  constructor(
        private router: Router,
        private authService: AuthService,
        private serviceOrderApiService: ServiceOrderApiService,
        private cdr: ChangeDetectorRef,
    ){
        this.searchSubscription = this.searchControl.valueChanges.pipe(
            map(value => value.trim()),
            tap((search) => {
                this.isSearchPending = search.length >= 3;
                this.cdr.markForCheck();
            }),
            filter(value => value.length === 0 || value.length >= 3),
            debounceTime(1000),
            distinctUntilChanged()
        ).subscribe((search) => {
            this.loadOrdersByActiveTab(1, 20, search);
        });
        this.loadOrdersByActiveTab();
    }
    ngOnInit() {
        // touchmove must be non-passive to allow preventDefault (stops native overscroll)
        this.touchMoveHandler = (e: TouchEvent) => this.onTouchMove(e);
        window.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
    }

    ngOnDestroy() {
        this.searchSubscription?.unsubscribe();
        this.loadOrdersSubscription?.unsubscribe();
        window.removeEventListener('touchmove', this.touchMoveHandler);
        clearTimeout(this.toastHideTimeout);
        clearTimeout(this.toastRemoveTimeout);
    }
  
  activeTab: 'pendentes' | 'recentes' = 'pendentes';
    searchControl = new FormControl('', { nonNullable: true });
    activeStatusFilter = 'Todos';
    availableStatusFilters: string[] = ['Todos'];
    isMenuOpen = false;
    isLoadingOrders = false;
    isLoadingMore = false;
    isSearchPending = false;
    loadOrdersError = '';
    isPulling = false;
    isSnapping = false;
    pullDistance = 0;
    private lastLoadKey = '';
    private readonly defaultPageSize = 20;
    private currentPage = 1;
    private readonly PULL_THRESHOLD = 70;
    private touchStartY = 0;
    private isTouchActive = false;
    private touchMoveHandler!: (e: TouchEvent) => void;
    hasNextPage = false;
    serviceOrders: ServiceOrder[] = [];
    filteredOrders: ServiceOrder[] = [];
    toast: { message: string; icon: string; variant: 'approve' | 'reject' } | null = null;
    toastLeaving = false;
    private toastHideTimeout?: ReturnType<typeof setTimeout>;
    private toastRemoveTimeout?: ReturnType<typeof setTimeout>;

  @HostListener('touchend')
    onTouchEnd() {
          if (!this.isTouchActive) return;
          this.isTouchActive = false;
          const shouldRefresh = this.readyToRefresh;
          // Snap back with CSS transition
          this.isSnapping = true;
          this.pullDistance = 0;
          this.isPulling = false;
          this.cdr.markForCheck();
          setTimeout(() => {
              this.isSnapping = false;
              this.cdr.markForCheck();
          }, 380);
          if (shouldRefresh) {
              this.lastLoadKey = '';
              this.loadOrdersByActiveTab();
          }
  }

  get greetingName(): string {
        const fullName = (this.authService.getUserName() || '').trim();
        if (!fullName) {
            return '';
        }
        return fullName.split(/\s+/)[0] || '';
  }

  private loadOrdersByActiveTab(
          page = 1,
          pageSize = this.defaultPageSize,
          overrideSearch?: string,
          append = false,
          resetStatusFilter = true) {
          const search = (overrideSearch ?? this.searchControl.value).trim();
          const status = search && this.activeStatusFilter !== 'Todos' ? this.activeStatusFilter : '';
          const loadKey = `${this.activeTab}:${page}:${pageSize}:${search}:${status}`;
          if (loadKey === this.lastLoadKey && !this.isLoadingOrders && !this.isLoadingMore) {
              this.isSearchPending = false;
              return;
          }
  
          this.lastLoadKey = loadKey;
          this.loadOrdersSubscription?.unsubscribe();
          this.isLoadingOrders = !append;
          this.isLoadingMore = append;
          this.loadOrdersError = '';
  
          if (!append) {
              this.scrollToTop();
              this.currentPage = 1;
              this.hasNextPage = false;
              this.serviceOrders = [];
              this.filteredOrders = [];
              if (resetStatusFilter) {
                  this.activeStatusFilter = 'Todos';
                  this.availableStatusFilters = ['Todos'];
              }
          }
          const onlyPending = true;
          const request$ = search
              ? this.serviceOrderApiService.search(search, page, pageSize, status, true)
              : this.activeTab === 'pendentes'
                  ? this.serviceOrderApiService.listPending(page, onlyPending, pageSize)
                  : this.serviceOrderApiService.list(page, onlyPending, pageSize);
  
          this.loadOrdersSubscription = request$
              .pipe(take(1))
              .subscribe({
                  next: (result) => {
                      const mappedItems = (result.items || []).map(item => this.mapApiOrderToCard(item));
                      this.currentPage = result.page || page;
                      this.hasNextPage = !!result.hasNextPage;
                      this.serviceOrders = append
                          ? [...this.serviceOrders, ...mappedItems]
                          : mappedItems;
                      if (search && this.activeStatusFilter === 'Todos') {
                          const mergedStatuses = Array.from(new Set(
                              this.serviceOrders
                                  .map(order => (order.status || '').trim())
                                  .filter(orderStatus => orderStatus.length > 0)
                          ));
                          this.availableStatusFilters = ['Todos', ...mergedStatuses];
                      }
                      this.filteredOrders = [...this.serviceOrders];
                      this.isLoadingOrders = false;
                      this.isLoadingMore = false;
                      this.isSearchPending = false;
                      this.cdr.markForCheck();
                  },
                  error: (error: unknown) => {
                      if (!append) {
                          this.serviceOrders = [];
                          this.filteredOrders = [];
                      }
                      this.loadOrdersError = this.getLoadOrdersErrorMessage(error);
                      this.isLoadingOrders = false;
                      this.isLoadingMore = false;
                      this.isSearchPending = false;
                      this.lastLoadKey = '';
                      this.cdr.markForCheck();
                  }
              });
  }

    onTouchMove(event: TouchEvent) {
        if (!this.isTouchActive || window.scrollY > 0) return;
        const diff = event.touches[0].clientY - this.touchStartY;
        if (diff > 0) {
            event.preventDefault();
            // Rubber-band: fast response at start, resistance increases as it stretches
            const max = this.PULL_THRESHOLD + 30;
            this.pullDistance = max * (1 - Math.exp(-diff / (max * 1.2)));
            this.isPulling = true;
            this.cdr.markForCheck();
        }
    }

  private mapApiOrderToCard(item: ServiceOrderListItemDto): ServiceOrder {
          const plate = (item.plate || '').trim();
          const vehicleName = [item.marca, item.vehicle].filter(Boolean).join(' ').trim();

          return {
              documentoId: item.documentoId,
              id: plate || `OS #${item.numero}`,
              vehicle: vehicleName || 'Veículo não identificado',
              year: item.vehicleYear ?? null,
              client: item.client || 'Não informado',
              technician: item.mechanicName || item.sellerName || 'Não atribuído',
              status: item.status || 'Aberto',
              rawStatus: item.status || 'Aberto',
              issuedAt: item.dtHrEmissao || null,
              itens: (item.oSItensLists || []).map((raw) => this.mapApiItemToCard(raw))
          };
  }

  private mapApiItemToCard(raw: oSItensLists): ServiceOrderItem {
          return {
              documentoItemId: raw.documentoItemId,
              codigo: raw.codigo || '',
              descricao: raw.descricao || '',
              quantidade: Number(raw.quantidade) || 0,
              valorOriginal: Number(raw.valorOriginal) || 0,
              desconto: Number(raw.desconto) || 0,
              total: Number(raw.total) || 0,
              flagAprovado: raw.flagAprovado,
              flagPendente: raw.flagPendente
          };
  }

  onApprovalRejectOs(action: DiscountItemAction): void {
        action.item.actionError = '';

        if (action.item.documentoItemId == null) {
            console.warn(`[os-approval] item "${action.item.codigo}" não tem documentoItemId — a API não retornou esse campo para ele.`);
            action.item.actionError = 'Não é possível aprovar/reprovar este item (dado ausente na API).';
            this.cdr.markForCheck();
            return;
        }

        const request: ApprovalOSRequest = {
            documentoItemId: action.item.documentoItemId,
            approvalOs: action.approvalOs
        };

        this.setItemProcessing(action.item, true);
        this.serviceOrderApiService.approvalOS(request)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    action.item.flagAprovado = action.approvalOs;
                    action.item.flagPendente = false;
                    this.setItemProcessing(action.item, false);
                    this.showToast(
                        action.approvalOs ? 'Desconto aprovado' : 'Desconto reprovado',
                        action.approvalOs ? 'check_circle' : 'cancel',
                        action.approvalOs ? 'approve' : 'reject'
                    );
                },
                error: (error: unknown) => {
                    console.error('[os-approval] falha ao aprovar/reprovar item', action.item.codigo, error);
                    action.item.actionError = 'Falha ao registrar. Tente novamente.';
                    this.setItemProcessing(action.item, false);
                }
            });
  }

  private setItemProcessing(item: ServiceOrderItem, isProcessing: boolean): void {
        item.isProcessing = isProcessing;
        this.cdr.markForCheck();
  }

  private showToast(message: string, icon: string, variant: 'approve' | 'reject'): void {
        clearTimeout(this.toastHideTimeout);
        clearTimeout(this.toastRemoveTimeout);

        this.toastLeaving = false;
        this.toast = { message, icon, variant };
        this.cdr.markForCheck();

        this.toastHideTimeout = setTimeout(() => {
            this.toastLeaving = true;
            this.cdr.markForCheck();

            this.toastRemoveTimeout = setTimeout(() => {
                this.toast = null;
                this.toastLeaving = false;
                this.cdr.markForCheck();
            }, 220);
        }, 1800);
  }

  private getLoadOrdersErrorMessage(error: unknown): string {
        if (error && typeof error === 'object' && 'name' in error && error.name === 'TimeoutError') {
            return 'Tempo limite excedido ao carregar as ordens de servico.';
        }

        if (error instanceof HttpErrorResponse) {
            if (error.status === 401 || error.status === 403) {
                return 'Sessao invalida para listar ordens de servico.';
            }

            if (error.status > 0) {
                return `Erro ${error.status} ao carregar ordens de servico.`;
            }
        }

        return 'Nao foi possivel carregar as ordens de servico.';
  }

  get readyToRefresh(): boolean {
      return this.pullDistance >= this.PULL_THRESHOLD;
  }

  
  get filialName(): string {
      return this.authService.getEmpresaDescricao() || 'Oficina';
  }
      
  toggleMenu() {
        this.isMenuOpen = !this.isMenuOpen;
  }

  closeMenu() {
        this.isMenuOpen = false;
  }
      
  get hasActiveSearch(): boolean {
        return this.searchControl.value.trim().length >= 3;
  }

  get isSearching(): boolean {
        return this.hasActiveSearch && (this.isSearchPending || this.isLoadingOrders);
  }

  submitSearch() {
        const search = this.searchControl.value.trim();
        if (search.length > 0 && search.length < 3) {
            return;
        }

        this.loadOrdersByActiveTab(1, 20, search);
  }

  backCreateOs(){
    this.router.navigate(['/home']);
  }

    setStatusFilter(status: string) {
        if (this.activeStatusFilter === status) {
            return;
        }

        this.activeStatusFilter = status;
        this.loadOrdersByActiveTab(1, this.defaultPageSize, this.searchControl.value.trim(), false, false);
    }

  setActiveTab(tab: 'pendentes' | 'recentes') {
        if (this.activeTab === tab) {
            return;
        }

        this.activeTab = tab;
        this.lastLoadKey = '';
        this.loadOrdersByActiveTab();
  }

      navigateToOrder(order: ServiceOrder): void {
        if (this.isDraftOrder(order)) {
            // Pass documentoId via both navigation state (reliable) and
            // sessionStorage (safety net if state is lost across guard redirects)
            sessionStorage.setItem('pendingDocumentoId', String(order.documentoId));
            this.router.navigate(['/new-os'], { state: { documentoId: order.documentoId } });
        } else {
            this.router.navigate(['/os-detail', order.documentoId]);
        }
    }

        private isDraftOrder(order: ServiceOrder): boolean {
        if (this.activeTab === 'pendentes' && !this.hasActiveSearch) {
            return true;
        }

        const normalizedStatus = (order.rawStatus || order.status || '').toLowerCase().trim();
        return normalizedStatus.includes('rascunho');
    }

  private scrollToTop(): void {
        window.scrollTo({ top: 0, behavior: 'auto' });
  }
}

